import { useEffect, useRef, useCallback } from "react";

import { usePageVisibility } from "./usePageVisibility";

// ============================================
// Types
// ============================================

export interface VisibilityAwareCallbackParams {
  /** Whether the app is actively being used (tab visible AND window focused) */
  isActive: boolean;
  /** Whether the page/tab is currently visible */
  isVisible: boolean;
  /** Whether the browser window currently has focus */
  isFocused: boolean;
  /** Whether this call was triggered after returning from inactive state */
  isReturningToActive: boolean;
  /** 
   * How long the user was inactive before this callback (in milliseconds).
   * Only meaningful when isReturningToActive is true, otherwise 0.
   */
  inactiveDurationMs: number;
}

export interface UseVisibilityAwareIntervalOptions {
  /** Whether the interval is enabled. Default: true */
  enabled?: boolean;
  /**
   * Interval duration when the app is inactive (in milliseconds).
   * If not provided, uses the same interval as when active.
   */
  inactiveIntervalMs?: number;
  /** Whether to fire the callback immediately on mount. Default: false */
  fireImmediately?: boolean;
}

// ============================================
// Hook
// ============================================

/**
 * Hook that runs an interval with visibility and focus awareness.
 * Uses different intervals for active vs inactive states.
 *
 * Key behavior:
 * - When active: Uses `intervalMs` for polling
 * - When inactive: Uses `inactiveIntervalMs` for polling (or same as active if not specified)
 * - On returning to active: Does NOT immediately refresh. Instead, calculates the remaining
 *   time until the next tick (based on active interval) and waits for that time.
 *   This prevents spurious refreshes when briefly losing focus (e.g., opening menus/modals).
 *
 * "Active" means the tab is visible AND the browser window has focus.
 * This detects: tab switches, browser minimization, AND switching to other applications.
 *
 * @param callback - Function to call on each interval tick, receives visibility state
 * @param intervalMs - Interval duration in milliseconds when app is active
 * @param options - Configuration options
 *
 * @example
 * ```tsx
 * // Poll every 60s when active, every 10 minutes when inactive
 * useVisibilityAwareInterval(
 *   ({ isActive, isReturningToActive, inactiveDurationMs }) => {
 *     if (isReturningToActive && inactiveDurationMs > 5 * 60 * 1000) {
 *       // Full refresh if user was away for more than 5 minutes
 *       fetchAllData();
 *     } else {
 *       // Regular refresh
 *       fetchNotifications();
 *     }
 *   },
 *   60000,
 *   { inactiveIntervalMs: 600000 }
 * );
 * ```
 */
export const useVisibilityAwareInterval = (
  callback: (params: VisibilityAwareCallbackParams) => void,
  intervalMs: number,
  options: UseVisibilityAwareIntervalOptions = {}
): void => {
  const {
    enabled = true,
    inactiveIntervalMs,
    fireImmediately = false,
  } = options;

  const { isActive, isVisible, isFocused } = usePageVisibility();

  // Store callback in ref to avoid re-running effect when callback changes
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  // Track the timestamp of the last callback execution
  const lastTickTimeRef = useRef<number>(Date.now());

  // Track if this is the initial mount
  const isInitialMountRef = useRef(true);

  // Track previous active state to detect returning to active
  const wasActiveRef = useRef(isActive);

  // Track when we became inactive (timestamp)
  const becameInactiveAtRef = useRef<number | null>(null);

  // Track timeoutId for cleanup
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ============================================
  // Track when we become inactive
  // ============================================

  useEffect(() => {
    if (!isActive && becameInactiveAtRef.current === null) {
      // Just became inactive - record the timestamp
      becameInactiveAtRef.current = Date.now();
    }
    // Note: We don't clear becameInactiveAtRef here - it gets cleared after callback execution
  }, [isActive]);

  // ============================================
  // Cleanup helper
  // ============================================

  const clearTimers = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    if (intervalIdRef.current !== null) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
  }, []);

  // ============================================
  // Execute callback helper
  // ============================================

  const createCallbackParams = useCallback(
    (isReturningToActive: boolean, inactiveDurationMs: number): VisibilityAwareCallbackParams => ({
      isActive,
      isVisible,
      isFocused,
      isReturningToActive,
      inactiveDurationMs,
    }),
    [isActive, isVisible, isFocused]
  );

  // ============================================
  // Main effect
  // ============================================

  useEffect(() => {
    // If disabled, clear timers and do nothing
    if (!enabled) {
      clearTimers();
      isInitialMountRef.current = true;
      wasActiveRef.current = isActive;
      becameInactiveAtRef.current = null;
      return;
    }

    // Determine current interval based on active state
    const effectiveInactiveInterval = inactiveIntervalMs ?? intervalMs;
    const currentIntervalMs = isActive ? intervalMs : effectiveInactiveInterval;

    // Check if we're returning to active state
    const wasActive = wasActiveRef.current;
    const isReturningToActive = !wasActive && isActive && !isInitialMountRef.current;

    // Calculate how long we were inactive
    const inactiveDurationMs = becameInactiveAtRef.current
      ? Date.now() - becameInactiveAtRef.current
      : 0;

    // Clear inactive timestamp if we're now active
    if (isActive) {
      becameInactiveAtRef.current = null;
    }

    // Update wasActive ref
    wasActiveRef.current = isActive;

    // Helper to execute callback and update last tick time
    const executeCallback = (returning: boolean, duration: number) => {
      lastTickTimeRef.current = Date.now();
      callbackRef.current(createCallbackParams(returning, duration));
    };

    // Helper to start regular interval (always passes false for isReturningToActive)
    const startInterval = () => {
      intervalIdRef.current = setInterval(() => {
        lastTickTimeRef.current = Date.now();
        callbackRef.current(createCallbackParams(false, 0));
      }, currentIntervalMs);
    };

    // Handle initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;

      if (fireImmediately) {
        executeCallback(false, 0);
      } else {
        // Set initial last tick time so first interval is correct
        lastTickTimeRef.current = Date.now();
      }

      // Start regular interval
      startInterval();

      return () => clearTimers();
    }

    // Not initial mount - we're responding to a change in active state or interval
    // Clear existing timers
    clearTimers();

    // Calculate time since last tick
    const now = Date.now();
    const timeSinceLastTick = now - lastTickTimeRef.current;

    // Calculate remaining time until next tick based on current interval
    const remainingTime = Math.max(0, currentIntervalMs - timeSinceLastTick);

    if (remainingTime > 0) {
      // Wait for remaining time, then execute and start regular interval
      timeoutIdRef.current = setTimeout(() => {
        // Only pass isReturningToActive=true on the FIRST callback after returning
        executeCallback(isReturningToActive, isReturningToActive ? inactiveDurationMs : 0);
        startInterval();
      }, remainingTime);
    } else {
      // Time already elapsed, execute immediately and start interval
      executeCallback(isReturningToActive, isReturningToActive ? inactiveDurationMs : 0);
      startInterval();
    }

    return () => clearTimers();
  }, [
    isActive,
    isVisible,
    isFocused,
    enabled,
    intervalMs,
    inactiveIntervalMs,
    fireImmediately,
    createCallbackParams,
    clearTimers,
  ]);
};